import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import {
    Badge,
    Button,
    ButtonGroup,
    Label,
    ListGroup,
    ListGroupItem,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
} from 'reactstrap';
import { DatePicker } from '../../components/DateTime';
import FiltersLabel from '../../components/FiltersLabel';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import Loading from '../../components/Loading';
import SearchBar from '../../components/SearchBar';
import Avatar from '../../components/Avatar';
import ExportModal from '../../components/ExportModal';
import CommunicationDetailPanel from './CommunicationDetailPanel';
import {
    fetchCommunications,
    fetchCommunicationById,
    clearSelectedCommunication,
} from '../../slices/communications.slice';
import { fetchOrganizations } from '../../slices/organizations.slice';
import { fetchUsers } from '../../slices/user.slice';
import { getFiterDatesLabel } from '../../utils/dates';
import { imgUrl } from '../../utils/api-request';
import api from '../../api';
import './Communications.scss';

const CATEGORIES = [
    { value: 'password-reset', label: 'Password Reset', icon: 'fa-key' },
    { value: 'welcome', label: 'Welcome', icon: 'fa-hand-wave' },
    { value: 'order-confirmation', label: 'Order Confirmation', icon: 'fa-receipt' },
    { value: 'order-status', label: 'Order Status', icon: 'fa-clipboard-check' },
    { value: 'order-placed', label: 'Order Placed', icon: 'fa-shopping-cart' },
    { value: 'order-request', label: 'Order Request', icon: 'fa-file-signature' },
    { value: 'order-approval', label: 'Order Approval', icon: 'fa-handshake' },
    { value: 'order-declined', label: 'Order Declined', icon: 'fa-ban' },
    { value: 'order-reverted', label: 'Order Reverted', icon: 'fa-rotate-left' },
    { value: 'system-notification', label: 'System Notification', icon: 'fa-bell' },
    { value: 'raw', label: 'Raw', icon: 'fa-code' },
];

// 'queued' is awaiting retry, not a failure — it must not read as one.
const STATUS_ICONS = {
    sent: 'fa-check',
    queued: 'fa-clock',
    failed: 'fa-times',
};

const Communications = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { commId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();

    const { list: communications, status, pagination } = useSelector(state => state.communications);
    const { list: users } = useSelector(state => state.users);
    const { list: organizations } = useSelector(state => state.organizations);

    const [searchValue, setSearchInput] = useState(searchParams.get('search') || '');
    const [searchModalValue, setSearchModalValue] = useState('');
    const debounceRef = useRef(null);
    const [filterModal, setFilterModal] = useState({ isOpen: false, filter: '' });
    const [exportModal, setExportModal] = useState(false);

    const isPanelOpen = !!commId;

    const selectCommunication = (id) => {
        navigate(`/communications/${id}${window.location.search}`);
    };

    const closePanel = () => {
        dispatch(clearSelectedCommunication());
        navigate(`/communications${window.location.search}`);
    };

    useEffect(() => {
        if (commId) {
            dispatch(fetchCommunicationById(commId));
        }
    }, [dispatch, commId]);

    const parseURLFilters = () => {
        const usersParam = searchParams.get('users');
        const organizationsParam = searchParams.get('organizations');
        const categoriesParam = searchParams.get('category');
        const statusParam = searchParams.get('status');
        const dateFromParam = searchParams.get('dateFrom');
        const dateToParam = searchParams.get('dateTo');
        const searchParam = searchParams.get('search');
        const pageParam = searchParams.get('page');

        return {
            users: usersParam ? usersParam.split(',') : [],
            organizations: organizationsParam ? organizationsParam.split(',') : [],
            category: categoriesParam ? categoriesParam.split(',') : [],
            status: statusParam || '',
            createdAt: {
                from: dateFromParam ? new Date(dateFromParam) : null,
                to: dateToParam ? new Date(dateToParam) : null,
            },
            search: searchParam || '',
            page: pageParam ? parseInt(pageParam, 10) : 1,
        };
    };

    const [filterBy, setFilterBy] = useState(parseURLFilters());

    const toggleFilterModal = (filter) => {
        setFilterModal({ isOpen: !filterModal.isOpen, filter });
    };

    const formatDateForURL = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const updateURLParams = (newFilters) => {
        const params = new URLSearchParams();
        if (newFilters.users.length > 0) params.set('users', newFilters.users.join(','));
        if (newFilters.organizations.length > 0) params.set('organizations', newFilters.organizations.join(','));
        if (newFilters.category.length > 0) params.set('category', newFilters.category.join(','));
        if (newFilters.status) params.set('status', newFilters.status);
        if (newFilters.createdAt.from) params.set('dateFrom', formatDateForURL(newFilters.createdAt.from));
        if (newFilters.createdAt.to) params.set('dateTo', formatDateForURL(newFilters.createdAt.to));
        if (newFilters.search) params.set('search', newFilters.search);
        if (newFilters.page && newFilters.page !== 1) params.set('page', newFilters.page);
        setSearchParams(params);
    };

    const handleSearch = (event) => {
        event.preventDefault();
        const query = event.target.value;
        setSearchInput(query);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setFilterBy(prev => {
                const newFilters = { ...prev, search: query, page: 1 };
                updateURLParams(newFilters);
                return newFilters;
            });
        }, 400);
    };

    const toggleArrayFilter = (filterKey, value) => {
        setFilterBy(prev => {
            const isSelected = prev[filterKey].includes(value);
            const newFilters = {
                ...prev,
                [filterKey]: isSelected
                    ? prev[filterKey].filter(v => v !== value)
                    : [...prev[filterKey], value],
                page: 1,
            };
            updateURLParams(newFilters);
            return newFilters;
        });
    };

    const setStatusFilter = (value) => {
        setFilterBy(prev => {
            const newFilters = { ...prev, status: prev.status === value ? '' : value, page: 1 };
            updateURLParams(newFilters);
            return newFilters;
        });
    };

    const handleCreatedAt = (period) => (date) => {
        setFilterBy(prev => {
            const newFilters = {
                ...prev,
                createdAt: { ...prev.createdAt, [period]: date },
                page: 1,
            };
            updateURLParams(newFilters);
            return newFilters;
        });
    };

    const areFiltersActive = filterBy.users.length > 0 ||
        filterBy.organizations.length > 0 ||
        filterBy.category.length > 0 ||
        filterBy.status ||
        filterBy.createdAt.from ||
        filterBy.createdAt.to;

    const clearFilters = () => {
        const newFilters = {
            users: [], organizations: [], category: [], status: '',
            createdAt: { from: null, to: null }, search: '', page: 1,
        };
        setFilterBy(newFilters);
        updateURLParams(newFilters);
    };

    const handlePageChange = (newPage) => {
        const newFilters = { ...filterBy, page: newPage };
        setFilterBy(newFilters);
        updateURLParams(newFilters);
    };

    const handleExport = async (fields, filetype = 'csv') => {
        try {
            const params = { ...filterBy, export: filetype, fields };
            const response = await api.get('/communications', { params, responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `communications.${filetype}`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            toast.error('Export failed');
            console.error('Error downloading file:', error);
        }
    };

    useEffect(() => {
        const params = {
            users: filterBy.users,
            organizations: filterBy.organizations,
            category: filterBy.category.join(',') || undefined,
            status: filterBy.status || undefined,
            page: filterBy.page,
            search: filterBy.search || undefined,
        };
        if (filterBy.createdAt.from) {
            const from = new Date(filterBy.createdAt.from);
            from.setHours(0, 0, 0, 0);
            params.from = from.toISOString();
        }
        if (filterBy.createdAt.to) {
            const to = new Date(filterBy.createdAt.to);
            to.setHours(23, 59, 59, 999);
            params.to = to.toISOString();
        }
        dispatch(fetchCommunications(params));
    }, [dispatch, filterBy]);

    useEffect(() => {
        dispatch(fetchOrganizations());
        dispatch(fetchUsers());
    }, [dispatch]);

    useEffect(() => {
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, []);

    const filterTextUser = (user) =>
        [user.firstName, user.lastName, user.username, user.email]
            .some(f => f?.toLowerCase().includes(searchModalValue.toLowerCase()));

    return (
        <div className="communications-view page px-4 py-3 d-flex flex-column overflow-y-auto">
            <PageHeader
                title="Communications"
                searchBar={{ searchValue, handleSearch }}
            />

            <div className="d-flex justify-content-start align-items-center gap-3 flex-wrap">
                <FiltersLabel active={areFiltersActive} onClear={clearFilters}/>

                <ButtonGroup className="gap-2 flex-wrap">
                    <Button color="primary" outline size="sm" className="rounded-3"
                        onClick={() => toggleFilterModal('users')}>
                        {filterBy.users.length > 0 ? (
                            <Badge color="success" className="me-2 text-white">{filterBy.users.length}</Badge>
                        ) : (
                            <span className="fas fa-users me-2" />
                        )}
                        Users
                    </Button>

                    <Button color="primary" outline size="sm" className="rounded-3"
                        onClick={() => toggleFilterModal('organizations')}>
                        {filterBy.organizations.length > 0 ? (
                            <Badge color="success" className="me-2 text-white">{filterBy.organizations.length}</Badge>
                        ) : (
                            <span className="fas fa-building me-2" />
                        )}
                        Organizations
                    </Button>

                    <Button color="primary" outline size="sm" className="rounded-3"
                        onClick={() => toggleFilterModal('category')}>
                        {filterBy.category.length > 0 ? (
                            <Badge color="success" className="me-2 text-white">{filterBy.category.length}</Badge>
                        ) : (
                            <span className="fas fa-tag me-2" />
                        )}
                        Category
                    </Button>

                    <Button
                        color={filterBy.status === 'sent' ? 'success' : 'primary'}
                        outline={filterBy.status !== 'sent'}
                        size="sm"
                        className="rounded-3"
                        onClick={() => setStatusFilter('sent')}
                    >
                        <span className="fas fa-check-circle me-2" />
                        Sent
                    </Button>

                    <Button
                        color={filterBy.status === 'queued' ? 'warning' : 'primary'}
                        outline={filterBy.status !== 'queued'}
                        size="sm"
                        className="rounded-3"
                        onClick={() => setStatusFilter('queued')}
                    >
                        <span className="fas fa-clock me-2" />
                        Queued
                    </Button>

                    <Button
                        color={filterBy.status === 'failed' ? 'danger' : 'primary'}
                        outline={filterBy.status !== 'failed'}
                        size="sm"
                        className="rounded-3"
                        onClick={() => setStatusFilter('failed')}
                    >
                        <span className="fas fa-times-circle me-2" />
                        Failed
                    </Button>

                    <Button color="primary" outline size="sm" className="rounded-3"
                        onClick={() => toggleFilterModal('createdAt')}>
                        {filterBy.createdAt.from || filterBy.createdAt.to ? (
                            <Badge color="success" className="me-2 text-white">
                                <span className="fas fa-calendar-check" />
                            </Badge>
                        ) : (
                            <span className="fas fa-calendar-week me-2" />
                        )}
                        {getFiterDatesLabel(filterBy.createdAt, 'Dates')}
                    </Button>
                </ButtonGroup>

                <div className="flex-grow-1">
                    <span className="fas fa-list me-2 text-primary" />
                    showing {pagination.totalCount} result{pagination.totalCount === 1 ? '' : 's'}
                </div>

                <div className="text-primary">
                    <Button color="link" onClick={() => setExportModal(true)} className="text-decoration-none">
                        <span className="fas fa-file-excel me-1" /> Export
                    </Button>
                </div>
            </div>

            {status === 'loading' && <Loading length={3} />}

            {status === 'success' && (
                <div className="communication-list">
                    {communications.map((comm) => (
                        <div
                            key={comm._id}
                            className={`communication-item my-2 p-3 bg-broken border-start d-flex justify-content-between status-${comm.status} ${commId === comm._id ? 'selected' : ''}`}
                            onClick={() => selectCommunication(comm._id)}
                        >
                            <div className="status-icon">
                                <span className={`fas ${STATUS_ICONS[comm.status] || 'fa-times'}`} />
                            </div>

                            <div className="flex-grow-1">
                                <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                    <span className="fw-semibold text-white">{comm.subject}</span>
                                    {comm.templateCode && (
                                        <Badge color="secondary" style={{ borderRadius: 0, fontSize: '0.7rem' }}>
                                            {comm.templateCode}
                                        </Badge>
                                    )}
                                </div>
                                <div className="small text-white-50">
                                    <span className="fas fa-envelope me-1" />
                                    {comm.to?.join(', ')}
                                </div>
                            </div>

                            <div className="d-flex flex-column align-items-end gap-2 ms-3">
                                <div className="small text-white-50">
                                    <span className="fas fa-calendar me-1" />
                                    {new Date(comm.createdAt).toLocaleString()}
                                </div>
                                {comm.organization && (
                                    <div className="small text-white-50">
                                        <span className="fas fa-building me-1" />
                                        {comm.organization.name}
                                    </div>
                                )}
                                {comm.user && (
                                    <div className="d-flex align-items-center gap-2 p-1 bg-black/30 rounded">
                                        <Avatar
                                            imageUrl={comm.user.profilePic?.imageUrl}
                                            name={`${comm.user.firstName} ${comm.user.lastName}`}
                                            size={22}
                                        />
                                        <span className="small">{comm.user.firstName} {comm.user.lastName}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}

                    {pagination.totalPages > 1 && (
                        <div className="d-flex justify-content-center align-items-center gap-3 my-4">
                            <Button
                                color="primary" outline size="sm"
                                disabled={pagination.currentPage === 1}
                                onClick={() => handlePageChange(pagination.currentPage - 1)}
                            >
                                <span className="fas fa-chevron-left me-2" /> Previous
                            </Button>
                            <span className="text-white">
                                Page {pagination.currentPage} of {pagination.totalPages}
                            </span>
                            <Button
                                color="primary" outline size="sm"
                                disabled={!pagination.hasMore}
                                onClick={() => handlePageChange(pagination.currentPage + 1)}
                            >
                                Next <span className="fas fa-chevron-right ms-2" />
                            </Button>
                        </div>
                    )}

                    <div className="status-legend">
                        <div className="legend-item sent">
                            <div className="legend-dot"><span className="fas fa-check" /></div>
                            Sent
                        </div>
                        <div className="legend-item failed">
                            <div className="legend-dot"><span className="fas fa-times" /></div>
                            Failed
                        </div>
                    </div>
                </div>
            )}

            <Modal isOpen={filterModal.isOpen} toggle={() => toggleFilterModal()} size="lg" scrollable>
                <ModalHeader toggle={() => toggleFilterModal()}>
                    Filter by {filterModal.filter}
                </ModalHeader>
                <ModalBody>
                    {filterModal.filter === 'users' && (
                        <div>
                            <SearchBar onChange={({ target }) => setSearchModalValue(target.value)} initialValue={searchModalValue} />
                            <ListGroup>
                                {users.map(user => filterTextUser(user) && (
                                    <ListGroupItem
                                        onClick={() => toggleArrayFilter('users', user._id)}
                                        key={user._id}
                                        style={{ cursor: 'pointer', borderRadius: 0 }}
                                        active={filterBy.users.includes(user._id)}
                                        className="border-0 border-start border-primary border-2 position-relative p-3 bg-dark
                                            text-white d-flex justify-content-start gap-3 align-items-center mb-2"
                                    >
                                        <Avatar
                                            imageUrl={user.profilePic?.imageUrl}
                                            name={`${user.firstName} ${user.lastName}`}
                                            size={44}
                                        />
                                        <div className="flex-grow-1">
                                            <span className="text-primary me-3">{user.firstName} {user.lastName}</span>
                                            (<code className="text-white">{user.username}</code>)
                                            {user.parentOrganization && (
                                                <p className="small mb-0">
                                                    <span className="fas fa-building me-2" />
                                                    {user.parentOrganization.name}
                                                </p>
                                            )}
                                        </div>
                                        {filterBy.users.includes(user._id) && (
                                            <div className="position-absolute top-0 end-0 mt-2 me-3 small">
                                                Selected <span className="fas fa-check-circle text-success" />
                                            </div>
                                        )}
                                    </ListGroupItem>
                                ))}
                            </ListGroup>
                        </div>
                    )}

                    {filterModal.filter === 'organizations' && (
                        <div>
                            <SearchBar onChange={({ target }) => setSearchModalValue(target.value)} initialValue={searchModalValue} />
                            <ListGroup>
                                {organizations.map(({ _id, name, logo }) => name.toLowerCase().includes(searchModalValue.toLowerCase()) && (
                                    <ListGroupItem
                                        onClick={() => toggleArrayFilter('organizations', _id)}
                                        key={_id}
                                        style={{ cursor: 'pointer', borderRadius: 0 }}
                                        active={filterBy.organizations.includes(_id)}
                                        className="border-0 border-start border-primary border-2 position-relative
                                            text-white d-flex justify-content-start gap-3 align-items-center mb-2"
                                    >
                                        <img src={imgUrl(logo?.imageUrl)} width={50} alt="" />
                                        <div className="flex-grow-1">{name}</div>
                                        {filterBy.organizations.includes(_id) && (
                                            <div className="position-absolute top-0 end-0 mt-2 me-3 small">
                                                Selected <span className="fas fa-check-circle text-success" />
                                            </div>
                                        )}
                                    </ListGroupItem>
                                ))}
                            </ListGroup>
                        </div>
                    )}

                    {filterModal.filter === 'category' && (
                        <ListGroup>
                            {CATEGORIES.map(({ value, label, icon }) => (
                                <ListGroupItem
                                    key={value}
                                    onClick={() => toggleArrayFilter('category', value)}
                                    style={{ cursor: 'pointer', borderRadius: 0 }}
                                    active={filterBy.category.includes(value)}
                                    className="border-0 border-start border-primary border-2 position-relative p-3 bg-dark
                                        text-white d-flex justify-content-start gap-3 align-items-center mb-2"
                                >
                                    <span className={`fas ${icon} text-primary`} />
                                    <div className="flex-grow-1 fw-medium">{label}</div>
                                    {filterBy.category.includes(value) && (
                                        <div className="position-absolute top-0 end-0 mt-2 me-3 small">
                                            Selected <span className="fas fa-check-circle text-success" />
                                        </div>
                                    )}
                                </ListGroupItem>
                            ))}
                        </ListGroup>
                    )}

                    {filterModal.filter === 'createdAt' && (
                        <div>
                            <Label>Date from</Label>
                            <DatePicker
                                value={filterBy.createdAt.from}
                                onChange={handleCreatedAt('from')}
                                placeholder="From"
                                clearable
                            />
                            <Label className="mt-4">Date to</Label>
                            <DatePicker
                                value={filterBy.createdAt.to}
                                onChange={handleCreatedAt('to')}
                                placeholder="To"
                                clearable
                            />
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={() => toggleFilterModal()}>
                        <span className="fas fa-check-circle me-2" /> Show results
                    </Button>
                </ModalFooter>
            </Modal>

            {isPanelOpen && (
                <CommunicationDetailPanel
                    commId={commId}
                    onClose={closePanel}
                    filterBy={filterBy}
                />
            )}

            <ExportModal
                isOpen={exportModal}
                toggle={() => setExportModal(false)}
                onExport={handleExport}
                entityName="Communications"
                fields={['subject', 'status', 'category', 'templateCode', 'from', 'createdAt', 'sesMessageId', 'errorMessage']}
                subFields={{
                    user: ['firstName', 'lastName', 'email'],
                    organization: ['name'],
                }}
                defaultSelectedFields={['subject', 'status', 'category', 'createdAt', 'user.email', 'organization.name']}
            />
        </div>
    );
};

export default Communications;
